import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ChevronLeft,
  ChevronRight,
  GitMerge,
  Grid2X2,
  List,
  Loader2,
  Search,
  Wrench,
  X,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useContacts, useContactTags, useToolWatchAlerts } from '@/hooks/useContacts';
import { useSetting } from '@/hooks/useSettings';
import { useUsers } from '@/hooks/useUsers';
import { cn } from '@/lib/utils';

import {
  CHANNEL_OPTIONS,
  getSavedFilters,
  hasStoredFilters,
  parseDefaultFilters,
  saveFilters,
  type ChannelFilter,
  type OwnerFilter,
} from '../filters';

import { ContactListRow } from './ContactListRow';
import { DuplicateContactsDialog } from './DuplicateContactsDialog';
import { TagFilterPopover } from './TagFilterPopover';

import type { ContactQuery } from '@repo/shared-types';

const PAGE_SIZE = 20;

const ICON_BUTTON_CLASS =
  'h-8 w-8 text-[#54656f] hover:bg-[#d9dbdf] hover:text-[#111b21] dark:text-[#aebac1] dark:hover:bg-[#2a3942] dark:hover:text-[#e9edef]';

export const ContactListPane = ({
  selectedId,
  selectedIds,
  onSelect,
  viewMode,
  onToggleViewMode,
}: {
  selectedId?: string;
  selectedIds?: string[];
  onSelect: (id: string) => void;
  viewMode?: 'single' | 'multi';
  onToggleViewMode?: () => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>(() => getSavedFilters().ownerFilter);
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>(
    () => getSavedFilters().channelFilter,
  );
  const [activeToolsFilter, setActiveToolsFilter] = useState(() => getSavedFilters().activeTools);
  // Specific-user assignment filter ('' = anyone); overrides the segmented control.
  const [assigneeFilter, setAssigneeFilter] = useState(() => getSavedFilters().assigneeFilter);
  // At most one tag per type; a contact must carry all selected tags.
  const [tagFilters, setTagFilters] = useState<string[]>(() => getSavedFilters().tagFilters);

  // Admin-defined defaults (Settings → Sales Console) seed the filters only on
  // a browser that has never saved its own — captured before the auto-save
  // effect below writes localStorage on mount. Applied via the render-time
  // state-adjustment pattern, since the setting value arrives async.
  const [hadStoredFilters] = useState(hasStoredFilters);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const { data: defaultFiltersSetting } = useSetting('SALES_CONSOLE_DEFAULT_FILTERS');
  if (!hadStoredFilters && !defaultsApplied) {
    const defaults = parseDefaultFilters(defaultFiltersSetting?.value);
    if (defaults) {
      setDefaultsApplied(true);
      setOwnerFilter(defaults.ownerFilter);
      setChannelFilter(defaults.channelFilter);
      setActiveToolsFilter(defaults.activeTools);
      setAssigneeFilter(defaults.assigneeFilter);
      setTagFilters(defaults.tagFilters);
    }
  }

  // Every filter choice is auto-saved and restored on the next visit.
  useEffect(() => {
    saveFilters({
      ownerFilter,
      channelFilter,
      activeTools: activeToolsFilter,
      assigneeFilter,
      tagFilters,
    });
  }, [ownerFilter, channelFilter, activeToolsFilter, assigneeFilter, tagFilters]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (searchInput === debouncedSearch) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      updateParams({ search: searchInput || null, page: null });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, updateParams, debouncedSearch]);

  const query = useMemo(() => {
    const q: Partial<ContactQuery> = { page, limit: PAGE_SIZE };
    if (debouncedSearch) q.search = debouncedSearch;
    if (assigneeFilter) q.assignedUserId = assigneeFilter;
    if (ownerFilter === 'active') q.hasInbound = true;
    if (channelFilter) q.channel = channelFilter;
    if (activeToolsFilter) q.activeTools = true;
    if (tagFilters.length > 0) q.tags = tagFilters.join(',');
    return q;
  }, [
    debouncedSearch,
    page,
    ownerFilter,
    channelFilter,
    activeToolsFilter,
    assigneeFilter,
    tagFilters,
  ]);

  const { data, isLoading } = useContacts(query);
  const { data: usersData } = useUsers({ limit: 100 });
  const { data: usedTags = [] } = useContactTags();
  // Fetch unacknowledged tool-watch alerts from server and merge into store on mount.
  useToolWatchAlerts();
  const users = usersData?.data ?? [];
  const contacts = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;

  return (
    <div className="flex h-full flex-col bg-white dark:bg-[#111b21]">
      <div className="space-y-2 border-b border-[#d1d7db] bg-[#f0f2f5] px-3 pt-3 pb-2.5 dark:border-[#2a3942] dark:bg-[#202c33]">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold tracking-tight text-[#111b21] dark:text-[#e9edef]">
            Sales Console
          </h1>
          <div className="flex items-center gap-1">
            {onToggleViewMode && (
              <Button
                variant="ghost"
                size="icon"
                className={ICON_BUTTON_CLASS}
                title={viewMode === 'single' ? 'Switch to multi-chat' : 'Switch to single chat'}
                onClick={onToggleViewMode}
              >
                {viewMode === 'single' ? (
                  <Grid2X2 className="h-4 w-4" />
                ) : (
                  <List className="h-4 w-4" />
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                ICON_BUTTON_CLASS,
                activeToolsFilter &&
                  'bg-[#00a884]/15 text-[#00a884] hover:text-[#00a884] dark:text-[#00a884] dark:hover:text-[#00a884]',
              )}
              title="Active tools — only chats where a watched tool fired in the last 7 days"
              onClick={() => {
                setActiveToolsFilter((v) => !v);
                updateParams({ page: null });
              }}
            >
              <Wrench className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={ICON_BUTTON_CLASS}
              title="Find duplicates"
              onClick={() => setDuplicatesOpen(true)}
            >
              <GitMerge className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[#667781] dark:text-[#8696a0]" />
          <input
            type="text"
            placeholder="Search name, phone, email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-9 w-full rounded-lg border-0 bg-white py-1.5 pr-8 pl-10 text-sm text-[#111b21] placeholder:text-[#667781] focus:ring-1 focus:ring-[#00a884] focus:outline-none dark:bg-[#2a3942] dark:text-[#e9edef] dark:placeholder:text-[#8696a0]"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="absolute top-1/2 right-2.5 -translate-y-1/2 cursor-pointer text-[#667781] transition-colors duration-150 hover:text-[#111b21] dark:text-[#8696a0] dark:hover:text-[#e9edef]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex flex-1 items-center gap-0.5 rounded-lg bg-[#e4e6e9] p-0.5 dark:bg-[#2a3942]">
            {(['active', 'all'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => {
                  setOwnerFilter(f);
                  setAssigneeFilter('');
                  updateParams({ page: null });
                }}
                className={cn(
                  'flex-1 cursor-pointer rounded-md px-2 py-1.5 text-xs font-medium transition-colors duration-150',
                  ownerFilter === f && !assigneeFilter
                    ? 'bg-[#00a884] text-white shadow-sm'
                    : 'text-[#667781] hover:text-[#111b21] dark:text-[#8696a0] dark:hover:text-[#e9edef]',
                )}
              >
                {f === 'active' ? 'Active' : 'All'}
              </button>
            ))}
          </div>
          <select
            value={channelFilter}
            onChange={(e) => {
              setChannelFilter(e.target.value as ChannelFilter);
              updateParams({ page: null });
            }}
            title="Filter by channel"
            className={cn(
              'h-8 min-w-0 flex-1 cursor-pointer rounded-md border-0 bg-white px-2 text-xs focus:ring-1 focus:ring-[#00a884] focus:outline-none dark:bg-[#2a3942]',
              channelFilter ? 'text-[#00a884]' : 'text-[#667781] dark:text-[#8696a0]',
            )}
          >
            {CHANNEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={assigneeFilter}
            onChange={(e) => {
              setAssigneeFilter(e.target.value);
              if (e.target.value) setOwnerFilter('all');
              updateParams({ page: null });
            }}
            title="Filter by assigned user"
            className={cn(
              'h-7 min-w-0 flex-1 cursor-pointer rounded-md border-0 bg-white px-2 text-xs focus:ring-1 focus:ring-[#00a884] focus:outline-none dark:bg-[#2a3942]',
              assigneeFilter ? 'text-[#00a884]' : 'text-[#667781] dark:text-[#8696a0]',
            )}
          >
            <option value="">Assignee: anyone</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name || user.email}
              </option>
            ))}
          </select>
          <TagFilterPopover
            selected={tagFilters}
            onChange={(next) => {
              setTagFilters(next);
              updateParams({ page: null });
            }}
            extraTags={usedTags}
          />
          {(assigneeFilter || tagFilters.length > 0) && (
            <button
              type="button"
              title="Clear filters"
              onClick={() => {
                setAssigneeFilter('');
                setTagFilters([]);
                updateParams({ page: null });
              }}
              className="cursor-pointer text-[#667781] transition-colors duration-150 hover:text-[#111b21] dark:text-[#8696a0] dark:hover:text-[#e9edef]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-[#667781] dark:text-[#8696a0]" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#667781] dark:text-[#8696a0]">
            No contacts found.{searchInput ? ' Try adjusting your search.' : ''}
          </div>
        ) : (
          contacts.map((contact) => (
            <ContactListRow
              key={contact.id}
              contact={contact}
              selected={contact.id === selectedId || !!selectedIds?.includes(contact.id)}
              onSelect={onSelect}
            />
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-[#d1d7db] px-2 py-1 dark:border-[#2a3942]">
          <Button
            variant="ghost"
            size="icon"
            className={cn(ICON_BUTTON_CLASS, 'h-7 w-7')}
            title="Previous page"
            disabled={page <= 1}
            onClick={() => updateParams({ page: page - 1 > 1 ? String(page - 1) : null })}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-[#667781] tabular-nums dark:text-[#8696a0]">
            {page} / {totalPages}
            <span className="text-[#667781]/60 dark:text-[#8696a0]/60">
              {' '}
              · {total.toLocaleString()}
            </span>
          </span>
          <Button
            variant="ghost"
            size="icon"
            className={cn(ICON_BUTTON_CLASS, 'h-7 w-7')}
            title="Next page"
            disabled={page >= totalPages}
            onClick={() => updateParams({ page: String(page + 1) })}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <DuplicateContactsDialog open={duplicatesOpen} onOpenChange={setDuplicatesOpen} />
    </div>
  );
};
